/**
 * phi-phone-code.js  —  Custom <phi-phone-code> element
 * Bordered combobox with label + text + chevron. No flags.
 */

(function () {
    'use strict';

    if (customElements.get('phi-phone-code')) return;

    class PhiPhoneCode extends HTMLElement {

        connectedCallback() {
            // Defer to next microtask so the element's children are fully parsed
            // (important when the script loads synchronously before the closing tag)
            if (this._connected) return;
            this._connected = true;

            this._trigger = this.querySelector('.phi-pcc__trigger');
            this._selectedText = this.querySelector('[data-phi-selected-text]');
            this._selectedFlag = this.querySelector('[data-phi-selected-flag]');
            this._listbox = this.querySelector('.phi-pcc__listbox');
            this._hiddenInput = this.querySelector('[data-phi-code-value]');
            this._options = Array.from(this.querySelectorAll('.phi-pcc__option'));

            if (!this._trigger || !this._listbox || !this._options.length) return;

            this._isOpen = false;
            this._focusedIndex = -1;

            // Bound refs for clean removeEventListener
            this._boundDocClick = this._onDocClick.bind(this);
            this._boundDocKey = this._onDocKeydown.bind(this);

            // Trigger events
            this._trigger.addEventListener('click', this._onTriggerClick.bind(this));
            this._trigger.addEventListener('keydown', this._onTriggerKeydown.bind(this));

            // Option events
            this._options.forEach((opt, idx) => {
                opt.addEventListener('mousedown', e => e.preventDefault()); // keep trigger focused
                opt.addEventListener('click', () => this._select(opt));
                opt.addEventListener('mousemove', () => { if (this._focusedIndex !== idx) this._setFocused(idx); });
            });

            // Document-level listeners (close on outside click / global keys)
            document.addEventListener('click', this._boundDocClick);
            document.addEventListener('keydown', this._boundDocKey);

            // Init display from pre-selected option
            const presel = this._options.find(o => o.classList.contains('phi-pcc__option--selected'));
            if (presel) this._applyDisplay(presel);
        }

        disconnectedCallback() {
            document.removeEventListener('click', this._boundDocClick);
            document.removeEventListener('keydown', this._boundDocKey);
            this._connected = false;
        }

        // ── Open / close ──────────────────────────────────────────────────────

        _openDropdown() {
            this._isOpen = true;
            this._trigger.setAttribute('aria-expanded', 'true');
            this._trigger.classList.add('phi-pcc__trigger--open');
            this._listbox.classList.add('phi-pcc__listbox--open');
            this._listbox.setAttribute('aria-hidden', 'false');
            // Highlight currently selected option
            const idx = this._options.findIndex(o => o.getAttribute('aria-selected') === 'true');
            this._setFocused(idx >= 0 ? idx : 0);
        }

        _closeDropdown() {
            this._isOpen = false;
            this._trigger.setAttribute('aria-expanded', 'false');
            this._trigger.classList.remove('phi-pcc__trigger--open');
            this._listbox.classList.remove('phi-pcc__listbox--open');
            this._listbox.setAttribute('aria-hidden', 'true');
            this._focusedIndex = -1;
            this._clearFocus();
        }

        // ── Select ────────────────────────────────────────────────────────────

        _select(optEl) {
            this._options.forEach(o => {
                o.setAttribute('aria-selected', 'false');
                o.classList.remove('phi-pcc__option--selected');
            });
            optEl.setAttribute('aria-selected', 'true');
            optEl.classList.add('phi-pcc__option--selected');
            this._applyDisplay(optEl);
            this._closeDropdown();
            this._trigger.focus();
        }

        _applyDisplay(optEl) {
            const label = optEl.dataset.label || optEl.textContent.trim();
            const value = optEl.dataset.value || '+1';
            const isoCode = optEl.dataset.flag || '';
            if (this._selectedText) this._selectedText.textContent = label;
            if (this._selectedFlag && isoCode) {
                // flag-icons uses CSS classes: "fi fi-{isoCode}"
                this._selectedFlag.className = 'phi-pcc__selected-flag fi fi-' + isoCode;
            }
            if (this._hiddenInput) this._hiddenInput.value = value;
        }

        // ── Focus management ─────────────────────────────────────────────────

        _setFocused(idx) {
            this._clearFocus();
            if (idx < 0 || idx >= this._options.length) return;
            this._focusedIndex = idx;
            this._options[idx].classList.add('phi-pcc__option--focused');
            requestAnimationFrame(() => this._options[idx].scrollIntoView({ block: 'nearest' }));
        }

        _clearFocus() {
            this._options.forEach(o => o.classList.remove('phi-pcc__option--focused'));
        }

        // ── Event handlers ────────────────────────────────────────────────────

        _onTriggerClick(e) {
            e.stopPropagation();
            this._isOpen ? this._closeDropdown() : this._openDropdown();
        }

        _onTriggerKeydown(e) {
            switch (e.key) {
                case 'Enter':
                case ' ':
                    e.preventDefault();
                    e.stopPropagation();
                    this._isOpen ? this._closeDropdown() : this._openDropdown();
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    if (!this._isOpen) { this._openDropdown(); break; }
                    this._setFocused(Math.min(this._focusedIndex + 1, this._options.length - 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    if (!this._isOpen) { this._openDropdown(); break; }
                    this._setFocused(Math.max(this._focusedIndex - 1, 0));
                    break;
                case 'Home':
                    if (this._isOpen) { e.preventDefault(); this._setFocused(0); }
                    break;
                case 'End':
                    if (this._isOpen) { e.preventDefault(); this._setFocused(this._options.length - 1); }
                    break;
                case 'Escape':
                    if (this._isOpen) { e.preventDefault(); this._closeDropdown(); }
                    break;
            }
        }

        _onDocKeydown(e) {
            if (!this._isOpen) return;
            if (e.key === 'Enter' && this._focusedIndex >= 0) {
                e.preventDefault();
                this._select(this._options[this._focusedIndex]);
            }
            if (e.key === 'Escape') {
                this._closeDropdown();
                this._trigger.focus();
            }
        }

        _onDocClick(e) {
            if (this._isOpen && !this.contains(e.target)) {
                this._closeDropdown();
            }
        }
    }

    customElements.define('phi-phone-code', PhiPhoneCode);
})();
